/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-unused-vars */
import {Divider, Form, Modal} from 'antd';
import React, {ReactNode, useEffect, useState} from 'react';

import {detectAutoDetect, drawerZIndex} from '../../../constants/Common';
import {APIType, APITypeEnum} from '../../../services/interface/tableAPIType';
import useCompanyStore from '../../../store/companyStore/companyDetailsStore';
import useUserStore from '../../../store/userStore/userStore';
import showMessage from '../../../utils/showMessage';
import Loader from '../../Loader/Loader';
import processTimeFrameData from '../../MonthlyYearly/Utils/processAllTimeFrame';

import {getFromAndTo, getFromAndToInData} from './getFromAndTo';

interface ModalFormProps {
  open: boolean;
  onCancel: () => void;
  FormComponent: ReactNode;
  title: string;
  setRefresh: React.Dispatch<React.SetStateAction<boolean>>;
  API?: (
    type: APIType,
    shouldCall?: boolean | any,
    data?: any,
    id?: string
  ) => Promise<any>;
  data?: any;
  idKey?: string;
  buttonText?: string;
  table?: boolean;
  type?: {key: string; value: string} | undefined;
  buttonDisabled?: {
    value: boolean;
    setter: React.Dispatch<React.SetStateAction<boolean>>;
  };
  fullScreen?: boolean;
  onlyYear?: boolean;
  isSupplierInfo?: boolean;
}

const ModalForm: React.FC<ModalFormProps> = ({
  open,
  onCancel,
  API,
  data = undefined,
  FormComponent,
  title,
  setRefresh,
  buttonText = 'Submit',
  idKey = '',
  table = false,
  type = undefined,
  buttonDisabled = null,
  fullScreen = false,
  onlyYear = false,
  isSupplierInfo = false
}) => {
  const [form] = Form.useForm();
  const userDetails = useUserStore.getState().userDetails;
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const companyId = useCompanyStore.getState().companyId;

  useEffect(() => {
    form.resetFields();
    if (data !== undefined) {
      form.setFieldsValue(data);
      setIsLoading(false);
      setIsSubmitDisabled(true);
    } else {
      setIsLoading(false);
      setIsSubmitDisabled(true);
    }
  }, [open, data]);

  const handleResponse = (res: any) => {
    if (res.status === 'error') {
      showMessage(res);
      setIsSubmitted(false);
      if (buttonDisabled) {
        buttonDisabled.setter(false);
      }
    } else {
      showMessage(res);
      onCancel();
      setRefresh(true);
      setIsSubmitted(false);
      if (buttonDisabled) {
        buttonDisabled.setter(true);
      }
    }
  };

  useEffect(() => {
    if (buttonDisabled) {
      setIsSubmitDisabled(buttonDisabled.value);
    }
  }, [buttonDisabled]);

  const onSubmit = (value: any) => {
    Object.keys(value).forEach((key) => {
      if (value[key] === undefined) {
        value[key] = null; // Replace with a suitable placeholder value
      }
    });

    if (isSupplierInfo) {
      value.company = companyId;
    }

    let ftData;

    if (value.year && value.facility) {
      ftData = getFromAndToInData(value.year, value.facility);
    } else {
      ftData = getFromAndTo(idKey);
    }

    const {year, fromDate, toDate, facilityId} = ftData;

    const mainData = processTimeFrameData(value, fromDate, toDate);

    if (type) {
      mainData[type.key] = type.value;
    }

    if (API && !idKey && !table && !onlyYear) {
      API(
        data?._id ? APITypeEnum.PUT : APITypeEnum.POST_NEW,
        true,
        {
          ...mainData,
          company: companyId,
          createdBy: userDetails._id,
          updatedBy: userDetails._id
        },
        data?._id ? data?._id : ''
      )
        .then((res) => {
          handleResponse(res);
        })
        .catch(() => {});
    } else if (API && table && facilityId && year && !onlyYear) {
      API(
        data?._id ? APITypeEnum.PUT : APITypeEnum.POST_NEW,
        {
          ...mainData,
          facility: facilityId,
          year,
          createdBy: userDetails._id,
          updatedBy: userDetails._id
        },
        data?._id ? data?._id : ''
      )
        .then((res) => {
          handleResponse(res);
        })
        .catch(() => {
          setIsSubmitted(false);
          if (buttonDisabled) {
            buttonDisabled.setter(false);
          }
        });
    } else if (API && table && year && onlyYear) {
      API(
        data?._id ? APITypeEnum.PUT : APITypeEnum.POST_NEW,
        {
          ...mainData,
          company: facilityId,
          year,
          createdBy: userDetails._id,
          updatedBy: userDetails._id
        },
        data?._id ? data?._id : ''
      )
        .then((res) => {
          handleResponse(res);
        })
        .catch(() => {
          setIsSubmitted(false);
          if (buttonDisabled) {
            buttonDisabled.setter(false);
          }
        });
    }
  };

  const onOk = () => {
    form.submit();
    setIsSubmitted(true);
  };

  return isLoading ? (
    <Loader />
  ) : (
    <Modal
      title={title}
      open={open}
      onCancel={onCancel}
      centered
      onOk={onOk}
      okButtonProps={{
        htmlType: 'submit',
        loading: isSubmitted,
        disabled: isSubmitDisabled
      }}
      okText={buttonText}
      maskClosable={false}
      width={fullScreen ? 'max-content' : '70%'}
      destroyOnClose={true}
      style={{
        overflow: 'scroll'
      }}
      zIndex={drawerZIndex}
    >
      <Form
        form={form}
        onFinish={onSubmit}
        onFinishFailed={() => {
          setIsSubmitted(false);
        }}
        onValuesChange={() => {
          setIsSubmitDisabled(false);
        }}
        autoComplete={detectAutoDetect()}
      >
        <Divider />
        {FormComponent}
        <Divider />
      </Form>
    </Modal>
  );
};

export default ModalForm;
